import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faTimes } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/setupAxios';

const NOTIFICATION_DURATION_MS = 10000; // 10 seconds
const MAX_NOTIFICATIONS_PER_POLL = 5; // guard against bulk DB updates surfacing as "new" songs

const LatestSongsNotifications = () => {
  const [activeSongs, setActiveSongs] = useState([]);
  const [showSongModal, setShowSongModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const lastTimestampRef = useRef(null);
  const seenSongIdsRef = useRef(new Set());
  const hideTimeoutRef = useRef(null);

  const fetchLatestSongs = async (since = null) => {
    try {
      const url = since
        ? `/songs/statistics/latest-songs?since=${encodeURIComponent(since)}`
        : '/songs/statistics/latest-songs';
      const res = await axiosInstance.get(url);

      if (res.data.success) {
        if (res.data.data?.latest_timestamp) {
          lastTimestampRef.current = res.data.data.latest_timestamp;
        }
        if (since && res.data.data?.songs && res.data.data.songs.length > 0) {
          processNewSongs(res.data.data.songs);
        }
      }
    } catch (error) {
      console.error('Error fetching latest songs:', error);
    }
  };

  const processNewSongs = (songs) => {
    const newSongs = songs.filter((song) => {
      if (!song.id) return false;
      if (seenSongIdsRef.current.has(song.id)) return false;
      seenSongIdsRef.current.add(song.id);
      return true;
    }).slice(0, MAX_NOTIFICATIONS_PER_POLL);
    if (newSongs.length === 0) return;

    setActiveSongs((prev) => {
      const next = [...prev];
      newSongs.forEach((song) => next.push({ id: song.id, song, addedAt: Date.now() }));
      return next;
    });

    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setActiveSongs([]);
      hideTimeoutRef.current = null;
    }, NOTIFICATION_DURATION_MS);
  };

  const handleCloseList = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setActiveSongs([]);
  };

  const handleRemoveSong = (id) => {
    setActiveSongs((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSongClick = (song) => {
    setSelectedSong(song);
    setShowSongModal(true);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    let intervalId = null;
    const initialize = async () => {
      await fetchLatestSongs();
      intervalId = setInterval(() => {
        if (lastTimestampRef.current) {
          fetchLatestSongs(lastTimestampRef.current);
        } else {
          fetchLatestSongs();
        }
      }, 5000);
    };
    initialize();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      {/* Each notification is its own box (no wrapper container). */}
      {/* Reverse the array so the newest notification is on top. */}
      {[...activeSongs].reverse().map((item, index) => {
        // Compute vertical position: newest (index 0) at 20px, older ones stacked below.
        const topPosition = 20 + (index * 90); // Each notification is spaced ~90px apart.
        
        return (
          <div
            key={item.id}
            className="latest-songs-popup-item rounded p-2"
            style={{
              position: 'fixed',
              top: `${topPosition}px`,
              right: '20px',
              zIndex: 9999 - index, // Newest notification has a higher z-index.
              minWidth: '320px',
              maxWidth: '400px',
              animation: 'slideInItem 0.35s ease-out',
              animationDelay: `${(activeSongs.length - 1 - index) * 0.05}s`,
              animationFillMode: 'backwards',
              cursor: 'pointer',
              backgroundColor: '#fff',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              borderRadius: '10px',
              border: '1px solid rgba(0,0,0,0.08)',
              margin: 0
            }}
            onClick={() => handleSongClick(item.song)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleSongClick(item.song)}
          >
            <div className="d-flex align-items-start">
              {item.song.thumbnail_url && (
                <img
                  src={item.song.thumbnail_url}
                  alt={item.song.title || item.song.name}
                  style={{
                    width: '44px',
                    height: '44px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    marginRight: '10px'
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex-grow-1 min-width-0">
                <div className="fw-bold text-truncate" style={{ fontSize: '0.95rem' }}>
                  {item.song.title || item.song.name || 'Untitled Track'}
                </div>
                <div className="d-flex gap-1 flex-wrap mt-1">
                  {item.song.type && (
                    <span className="badge bg-info" style={{ fontSize: '0.7rem' }}>{item.song.type}</span>
                  )}
                  {item.song.service && (
                    <span className="badge bg-secondary text-white" style={{ fontSize: '0.7rem' }}>{item.song.service}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-1 text-muted"
                style={{ fontSize: '0.85rem' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveSong(item.id);
                }}
                aria-label="Remove"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        );
      })}

      {/* Song Detail Modal */}
      {showSongModal && selectedSong && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => {
            setShowSongModal(false);
            setSelectedSong(null);
          }}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable modal-fullscreen-md-down"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Song Details</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowSongModal(false);
                    setSelectedSong(null);
                  }}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Thumbnail */}
                {selectedSong.thumbnail_url && (
                  <div className="mb-3 text-center">
                    <img
                      src={selectedSong.thumbnail_url}
                      alt={selectedSong.title || selectedSong.name}
                      style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Audio Player */}
                {selectedSong.audio_url && (
                  <div className="mb-3">
                    <h6 className="mb-2">Audio Player</h6>
                    <div className="bg-dark p-3 rounded">
                      <audio
                        controls
                        style={{ width: '100%' }}
                        preload="metadata"
                        key={selectedSong.audio_url}
                      >
                        <source src={selectedSong.audio_url} type="audio/mpeg" />
                        <source src={selectedSong.audio_url} type="audio/mp3" />
                        <source src={selectedSong.audio_url} type="audio/wav" />
                        <source src={selectedSong.audio_url} type="audio/m4a" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </div>
                )}

                {/* Song Information */}
                <div className="mb-3">
                  <h6 className="mb-2">Song Information</h6>
                  <table className="table table-sm table-bordered">
                    <tbody>
                      <tr>
                        <td><strong>ID:</strong></td>
                        <td>{selectedSong.id}</td>
                      </tr>
                      <tr>
                        <td><strong>Title:</strong></td>
                        <td>{selectedSong.title || selectedSong.name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td><strong>Type:</strong></td>
                        <td><span className="badge bg-info">{selectedSong.type || 'N/A'}</span></td>
                      </tr>
                      <tr>
                        <td><strong>Service:</strong></td>
                        <td><span className="badge bg-secondary text-white">{selectedSong.service || 'N/A'}</span></td>
                      </tr>
                      <tr>
                        <td><strong>Device ID:</strong></td>
                        <td><code style={{ fontSize: '11px' }}>{selectedSong.device_id || 'N/A'}</code></td>
                      </tr>
                      <tr>
                        <td><strong>Created At:</strong></td>
                        <td>{selectedSong.created_at ? new Date(selectedSong.created_at).toLocaleString() : 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Prompt */}
                {selectedSong.prompt && (
                  <div className="mb-3">
                    <h6 className="mb-2">Prompt</h6>
                    <div className="bg-light p-3 rounded">
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedSong.prompt}</p>
                    </div>
                  </div>
                )}

                {/* Lyrics */}
                {selectedSong.lyrics && selectedSong.lyrics !== selectedSong.prompt && (
                  <div className="mb-3">
                    <h6 className="mb-2">Lyrics</h6>
                    <div className="bg-light p-3 rounded">
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedSong.lyrics}</p>
                    </div>
                  </div>
                )}

                {/* Links */}
                <div className="mb-3">
                  <h6 className="mb-2">Links</h6>
                  <div className="d-flex flex-column gap-2">
                    {selectedSong.audio_url && (
                      <a
                        className="btn btn-outline-primary btn-sm"
                        href={selectedSong.audio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Audio URL
                      </a>
                    )}
                    {selectedSong.thumbnail_url && (
                      <a
                        className="btn btn-outline-secondary btn-sm"
                        href={selectedSong.thumbnail_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Thumbnail URL
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowSongModal(false);
                    setSelectedSong(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LatestSongsNotifications;
